import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import moment from 'moment';
import { scheduleSubTaskMarkCompleteAPI, scheduleSubTaskSkipAPI } from 'src/api/ScheduleTaskApi';
import CombinedTaskCard from './CombinedTaskCard';

const CombinedTasks = ({ tasks, filterSearch, handleSearch, assignee, taskId, onDelete, onEdit }) => {
  // const hierarchyTasks = tasks.filter(t => t.type === 'Hierarchy');
  const {
    search: { data = [] } = {},
    hierarchy = [],
    taskCompletionStatus = [],
    taskSkipStatus = [],
  } = useSelector((state) => state.ScheduleTaskReducer, shallowEqual);
  const daysOfWeek = ["M", "T", "W", "TH", "F", "SA", "SU"];
  const dayMap = {
    0: "SU",
    1: "M",
    2: "T",
    3: "W",
    4: "TH",
    5: "F",
    6: "SA"
  };
  const today = moment().format('YYYY-MM-DD');
  const todayDayChar = dayMap[moment().day()];

  // 1. Build Set of today's completed subtask IDs
  const completedTodaySet = new Set(
    taskCompletionStatus
      .filter(t => moment(t.date).format('YYYY-MM-DD') === today)
      .map(t => t.subTaskId?.toString())
  );
  const skippedTodaySet = new Set(
    taskSkipStatus
      .filter(t => moment(t.date).format('YYYY-MM-DD') === today)
      .map(t => t.subTaskId?.toString())
  );
  const hierarchyTasks = hierarchy
    .map(h => data.find(d => d._id?.toString() === h.subTaskId?.toString()))
    .filter((task) => !taskId || task?.taskId?._id?.toString() === taskId)
    .filter(task => {
      if (
        !task ||
        completedTodaySet.has(task._id?.toString()) ||
        skippedTodaySet.has(task._id?.toString()) ||
        task.initiationType !== 'Dependency' ||
        !(task.selectedDays || []).includes(todayDayChar)
      ) {
        return false;
      }

      // Apply matchSearch logic
      const matchSearch =
        !filterSearch ||
        task.taskId?.taskName?.toLowerCase().includes(filterSearch.toLowerCase()) ||
        task.subTaskName?.toLowerCase().includes(filterSearch.toLowerCase());

      // Apply matchAssignee logic
      const matchAssignee =
        !assignee ||
        (assignee === 'Self'
          ? task.assignTo === 'Self'
          : (task.users || []).some(u => u._id === assignee));

      return matchSearch && matchAssignee;
    })
    .map(task => ({
      type: 'Hierarchy',
      task: task.taskId?.taskName || 'Main Task',
      subtask: task.subTaskName,
      assignedTo: task.assignTo,
      subTaskId: task._id,
      ts: task.ts,
      assignedUser:
        (task.users.length || 0) > 0
          ? task.users.map((user) => user.firstName + " " + user.lastName).join(', ')
          : "NA",
      duration: `${task.duration} ${task.durationUnit}`,
    }));

  // const fixedTimeTasks = data
  //   .filter((task) => !taskId || task?.taskId?._id?.toString() === taskId)
  //   .filter(task => {
  //     if (
  //       task.initiationType !== 'Fixed' ||
  //       completedTodaySet.has(task._id?.toString()) ||
  //       skippedTodaySet.has(task._id?.toString()) ||
  //       !(task.selectedDays || []).includes(todayDayChar)
  //     ) {
  //       return false;
  //     }

  //     // matchSearch: filter by taskName or subTaskName
  //     const matchSearch =
  //       !filterSearch ||
  //       task.taskId?.taskName?.toLowerCase().includes(filterSearch.toLowerCase()) ||
  //       task.subTaskName?.toLowerCase().includes(filterSearch.toLowerCase());

  //     // matchAssignee: check if task is assigned to specified user or 'Self'
  //     const matchAssignee =
  //       !assignee ||
  //       (assignee === 'Self'
  //         ? task.assignTo === 'Self'
  //         : (task.users || []).some(u => u._id === assignee));

  //     return matchSearch && matchAssignee;
  //   })
  //   .map(task => ({
  //     type: 'Fixed',
  //     task: task.taskId?.taskName || 'Main Task',
  //     subtask: task.subTaskName,
  //     assignedTo: task.assignTo,
  //     subTaskId: task._id,
  //     ts: task.ts,
  //     assignedUser:
  //       (task.users.length || 0) > 0
  //         ? task.users.map((user) => user.firstName).join(', ')
  //         : "NA",
  //     duration: `${task.duration} ${task.durationUnit}`,
  //     startTime: task.time || 'N/A',
  //   }));



  // const anyTimeTasks = data
  //   .filter((task) => !taskId || task?.taskId?._id?.toString() === taskId)
  //   .filter(task => {
  //     if (
  //       task.initiationType !== 'Dependency' ||
  //       completedTodaySet.has(task._id?.toString()) ||
  //       skippedTodaySet.has(task._id?.toString()) ||
  //       !(task.selectedDays || []).includes(todayDayChar)
  //     ) {
  //       return false;
  //     }
  //     // matchSearch: filter by taskName or subTaskName
  //     const matchSearch =
  //       !filterSearch ||
  //       task.taskId?.taskName?.toLowerCase().includes(filterSearch.toLowerCase()) ||
  //       task.subTaskName?.toLowerCase().includes(filterSearch.toLowerCase());

  //     // matchAssignee: check if task is assigned to specified user or 'Self'
  //     const matchAssignee =
  //       !assignee ||
  //       (assignee === 'Self'
  //         ? task.assignTo === 'Self'
  //         : (task.users || []).some(u => u._id === assignee));

  //     return matchSearch && matchAssignee;
  //   })
  //   .map(task => ({
  //     type: 'AnyTime',
  //     task: task.taskId?.taskName || 'Main Task',
  //     subtask: task.subTaskName,
  //     assignedTo: task.assignTo,
  //     subTaskId: task._id,
  //     ts: task.ts,
  //     assignedUser:
  //       (task.users.length || 0) > 0
  //         ? task.users.map((user) => user.firstName).join(', ')
  //         : "NA",
  //     duration: `${task.duration} ${task.durationUnit}`,
  //     startTime: task.time || 'N/A',
  //   }));

  


  function normalizeSelectedDays(selectedDays) {
  if (!selectedDays) return [];
  if (Array.isArray(selectedDays)) return selectedDays.map(d => String(d).toUpperCase().trim());
  if (typeof selectedDays === "string") {
    return selectedDays
      .split(/[,;|\s]+/)
      .map(d => {
        const dd = d.trim().toUpperCase();
        if (dd === "MON" || dd === "M") return "M";
        if (dd === "TUE" || dd === "T") return "T";
        if (dd === "WED" || dd === "W") return "W";
        if (dd === "THU" || dd === "TH") return "TH";
        if (dd === "FRI" || dd === "F") return "F";
        if (dd === "SAT" || dd === "SA") return "SA";
        if (dd === "SUN" || dd === "SU") return "SU";
        return dd;
      })
      .filter(Boolean);
  }
  if (typeof selectedDays === "number") return [dayMap[selectedDays]];
  return [];
}

function normalizeSelectedMonthDates(selectedMonthDates) {
  if (!selectedMonthDates) return [];
  if (Array.isArray(selectedMonthDates)) return selectedMonthDates.map(d => String(d).trim());
  if (typeof selectedMonthDates === "string") {
    return selectedMonthDates.split(/[,;|\s]+/).map(d => String(d).trim()).filter(Boolean);
  }
  return [];
}

function isScheduledOnMoment(task, mDate) {
  // mDate is a moment object
  const dayChar = dayMap[mDate.day()];
  const dateStr = String(mDate.date()); // "1".."31"

  const sDays = normalizeSelectedDays(task.selectedDays);
  if (sDays.includes(dayChar)) return true;

  const sMonthDates = normalizeSelectedMonthDates(task.selectedMonthDates);
  console.log('dateStr', sMonthDates, dateStr,sMonthDates.includes(dateStr));
  if (sMonthDates.includes(dateStr)) return true;

  // Frequency-based default: Daily -> always scheduled
  if (task.frequency === "Daily") return true;

  return false;
}

// Return last scheduled date before today as "YYYY-MM-DD" per frequency rules
function getLastScheduledDateBeforeToday(task) {
  const freq = task.frequency || ""; // expected: "Daily", "Weekly", "Monthly", etc.

  // determine lookback window
  let lookback = 31;
  if (freq === "Daily") lookback = 1;
  else if (freq === "Weekly") lookback = 7;
  else if (freq === "Monthly") {
    // look back to start of CURRENT month only (do not consider previous months)
    const startOfMonth = moment().startOf('month');
    const daysSinceStart = moment().diff(startOfMonth, 'days');
    lookback = daysSinceStart === 0 ? 0 : daysSinceStart; // 0 means none
  } // else keep 31

  if (lookback <= 0) return null;

  for (let i = 1; i <= lookback; i++) {
    const candidate = moment().subtract(i, "days");
    if (isScheduledOnMoment(task, candidate)) {
      return candidate.format("YYYY-MM-DD");
    }
  }
  return null;
}

function idToString(idLike) {
  if (idLike === undefined || idLike === null) return "";
  try { return String(idLike); } catch { return ""; }
}

// Build completion/skip maps keyed by YYYY-MM-DD => Set(subTaskIdStrings)
function buildDateToIdSetMap(recordsArray) {
  const map = new Map();
  (recordsArray || []).forEach(r => {
    const d = moment(r.date).format("YYYY-MM-DD");
    const id = idToString(r.subTaskId || r._id || r.subTask?._id);
    if (!id) return;
    if (!map.has(d)) map.set(d, new Set());
    map.get(d).add(id);
  });
  return map;
}

const completedByDate = buildDateToIdSetMap(taskCompletionStatus);
const skippedByDate = buildDateToIdSetMap(taskSkipStatus);

// Common matchers
function matchesSearchAndAssignee(task) {
  const matchSearch =
    !filterSearch ||
    (task.taskId?.taskName || "").toLowerCase().includes(filterSearch.toLowerCase()) ||
    (task.subTaskName || "").toLowerCase().includes(filterSearch.toLowerCase());

  const matchAssignee =
    !assignee ||
    (assignee === "Self"
      ? task.assignTo === "Self"
      : (task.users || []).some((u) => u._id === assignee));

  return matchSearch && matchAssignee;
}

const baseMap = (type) => (task, extra = {}) => ({
  type,
  task: task.taskId?.taskName || "Main Task",
  subtask: task.subTaskName,
  assignedTo: task.assignTo,
  subTaskId: task._id,
  frequency: task.frequency,
  ts: task.ts,
  assignedUser:
    (task.users && task.users.length > 0)
      ? task.users.map((user) => user.firstName).join(", ")
      : "NA",
  duration: `${task.duration} ${task.durationUnit}`,
  startTime: task.time || "N/A",
  ...extra,
});

// ---------- filter factory supporting pending logic (per-frequency) ----------
function buildTaskFilterWithSmartPending(expectedInitiationType) {
  return (task) => {
    if (task.initiationType !== expectedInitiationType) return false;

    const subId = idToString(task._id);

    // Exclude if completed or skipped TODAY
    const completedTodaySet = completedByDate.get(today) || new Set();
    const skippedTodaySet = skippedByDate.get(today) || new Set();
    if (completedTodaySet.has(subId) || skippedTodaySet.has(subId)) return false;

    // If scheduled for today -> include (subject to search/assignee)
    const scheduledToday = isScheduledOnMoment(task, moment());
    if (scheduledToday) {
      return matchesSearchAndAssignee(task);
    }

    // Otherwise check last scheduled date BEFORE today according to frequency rules
    const lastScheduledDate = getLastScheduledDateBeforeToday(task); // "YYYY-MM-DD" or null
    if (!lastScheduledDate) return false;

    // Do not consider pending if lastScheduledDate is today or in future (shouldn't happen)
    if (lastScheduledDate >= today) return false;

    // If it was completed or skipped on that lastScheduledDate => not pending
    const completedSetOnLast = completedByDate.get(lastScheduledDate) || new Set();
    const skippedSetOnLast = skippedByDate.get(lastScheduledDate) || new Set();
    if (completedSetOnLast.has(subId) || skippedSetOnLast.has(subId)) return false;

    // Now it's a missed scheduled occurrence per frequency rules -> mark pending
    return matchesSearchAndAssignee(task);
  };
}




  // temp helper function
  const fixedTimeTasks = data
  .filter((task) => !taskId || task?.taskId?._id?.toString() === taskId)
  .filter(buildTaskFilterWithSmartPending("Fixed"))
  .map(task => {
    const subId = idToString(task._id);
    const lastScheduled = getLastScheduledDateBeforeToday(task);
    const isPending = lastScheduled && lastScheduled < today &&
                      !(completedByDate.get(lastScheduled) || new Set()).has(subId) &&
                      !(skippedByDate.get(lastScheduled) || new Set()).has(subId) &&
                      !isScheduledOnMoment(task, moment()); // ensure not scheduled today
    return baseMap("Fixed")(task, { isPending });
  });

const anyTimeTasks = data
  .filter((task) => !taskId || task?.taskId?._id?.toString() === taskId)
  .filter(buildTaskFilterWithSmartPending("Dependency"))
  .map(task => {
    const subId = idToString(task._id);
    const lastScheduled = getLastScheduledDateBeforeToday(task);
    const isPending = lastScheduled && lastScheduled < today &&
                      !(completedByDate.get(lastScheduled) || new Set()).has(subId) &&
                      !(skippedByDate.get(lastScheduled) || new Set()).has(subId) &&
                      !isScheduledOnMoment(task, moment());
    return baseMap("AnyTime")(task, { isPending });
  });
  const remainingCount = hierarchyTasks.length - 1;


  const handleMarkAsCompleted = async (subTaskId) => {
    await scheduleSubTaskMarkCompleteAPI({ data: { subTaskId } });
    handleSearch();
  };
  const handleSkipTask = async (subTaskId) => {
    await scheduleSubTaskSkipAPI({ data: { subTaskId } });
    handleSearch();
  };
  return (
    <div className="mb-4">
      {/* Hierarchy Tasks */}
      {/* {hierarchyTasks.length > 0 && (
        <>
          <h5 className="text-primary fw-bold mb-3 font-16">Hierarchy</h5>
          {hierarchyTasks.map((task, index) => (
            <CombinedTaskCard key={`hierarchy-${index}`} task={task} handleMarkAsCompleted={handleMarkAsCompleted} handleSkipTask={handleSkipTask} onDelete={onDelete} onEdit={onEdit} />
          ))}
        </>
      )} */}
      {anyTimeTasks.length > 0 && (
        <>
          <h5 className="text-primary fw-bold mb-3 font-16">Any Time</h5>
          {anyTimeTasks.map((task, index) => (
            <CombinedTaskCard key={`hierarchy-${index}`} task={task} handleMarkAsCompleted={handleMarkAsCompleted} handleSkipTask={handleSkipTask} onDelete={onDelete} onEdit={onEdit} />
          ))}
        </>
      )}

      {/* Fixed Tasks */}
      {fixedTimeTasks.length > 0 && (
        <>
          <h5 className="text-success fw-bold mt-4 mb-3 font-16">Fixed</h5>
          {fixedTimeTasks.map((task, index) => (
            <CombinedTaskCard key={`fixed-${index}`} task={task} handleMarkAsCompleted={handleMarkAsCompleted} handleSkipTask={handleSkipTask} onDelete={onDelete} onEdit={onEdit} />
          ))}
        </>
      )}

      {/* If both empty */}
      {hierarchyTasks.length === 0 && fixedTimeTasks.length === 0 && (
        <div className="text-muted">No Hierarchy or Fixed tasks available.</div>
      )}
    </div>
  );
};

export default CombinedTasks;
